import React, { useEffect, useState } from 'react';
import './PaymentMethod.css';
import { useLanguage } from './i18n/LanguageContext';
import { apiRequest, parseResponse, getLoggedInUserId } from './utils/api';
import { maskCardNumber, normalizeSavedCard } from './utils/cards';

const PaymentMethod = ({ selectedPaymentMethod, onSelectPaymentMethod, onBack, onViewSavedCards, onSelectSavedCard }) => {
  const { t } = useLanguage();
  const [selected, setSelected] = useState(selectedPaymentMethod || null);
  const [savedCards, setSavedCards] = useState([]);

  useEffect(() => {
    const userId = getLoggedInUserId();
    if (!userId) {
      return;
    }

    let cancelled = false;
    const loadCards = async () => {
      try {
        const response = await apiRequest(`/payments/saved-cards/${userId}`, { method: 'GET' });
        const data = await parseResponse(response);
        if (!response.ok || cancelled) {
          return;
        }
        const cards = Array.isArray(data) ? data : (data?.data || []);
        setSavedCards(cards.map(normalizeSavedCard).filter((card) => card.id));
      } catch (error) {
        console.warn('Unable to load saved cards', error);
      }
    };

    loadCards();
    return () => {
      cancelled = true;
    };
  }, []);

  const paymentMethods = [
    {
      id: 'upi',
      name: 'UPI (GPay, PhonePe)',
      subtitle: 'Pay via any UPI app',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
          <rect x="14" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
          <rect x="3" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
          <rect x="14" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
        </svg>
      )
    },
    {
      id: 'card',
      name: 'Credit / Debit Card',
      subtitle: 'Visa, Mastercard, Amex',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M2 10H22" stroke="currentColor" strokeWidth="2"/>
        </svg>
      )
    },
    {
      id: 'cash',
      name: 'Cash to driver',
      subtitle: 'Pay after trip ends',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="2" y="4" width="20" height="16" rx="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M2 8H22" stroke="currentColor" strokeWidth="2"/>
          <circle cx="6" cy="14" r="1.5" fill="currentColor"/>
          <circle cx="18" cy="14" r="1.5" fill="currentColor"/>
        </svg>
      )
    }
  ];

  const handleSelect = (method) => {
    setSelected(method.id);
    onSelectPaymentMethod(method.id);
  };

  return (
    <div className="payment-method-screen">
      <div className="payment-method-content">
        <header className="payment-header">
          <button className="back-button" onClick={onBack} aria-label="Go back">
            <img
              src={process.env.PUBLIC_URL + '/images/back_button.svg'}
              alt="Back"
            />
          </button>
          <h1 className="payment-title">{t('Payment method')}</h1>
        </header>

        {savedCards.length > 0 && (
          <section className="saved-cards-section">
            <div className="saved-cards-section-header">
              <h2 className="saved-cards-section-title">{t('Saved Cards')}</h2>
              {onViewSavedCards && (
                <button type="button" className="manage-cards-button" onClick={onViewSavedCards}>
                  {t('Manage')}
                </button>
              )}
            </div>
            <div className="payment-options">
              {savedCards.map((card) => (
                <button
                  type="button"
                  key={card.id}
                  className="payment-option saved-card-option"
                  onClick={() => onSelectSavedCard && onSelectSavedCard(card)}
                >
                  <div className="payment-icon">
                    <span className="card-type-badge">{card.cardType}</span>
                  </div>
                  <div className="payment-info">
                    <h3 className="payment-name">{maskCardNumber(card.last4)}</h3>
                    <p className="payment-subtitle">{card.name}</p>
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        <div className="payment-options">
          {paymentMethods.map((method) => (
            <div
              key={method.id}
              className={`payment-option ${selected === method.id ? 'selected' : ''}`}
              onClick={() => handleSelect(method)}
            >
              <div className="payment-icon">
                {method.icon}
              </div>
              <div className="payment-info">
                <h3 className="payment-name">{method.name}</h3>
                <p className="payment-subtitle">{method.subtitle}</p>
              </div>
              {selected === method.id && (
                <div className="selected-indicator">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" fill="#764CA2"/>
                    <path d="M9 12L11 14L15 10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PaymentMethod;
